import React, { useEffect, useMemo, useRef } from 'react'
import { TourProvider, useTour } from '@reactour/tour'

/**
 * Scroll the highlighted node into view in nested app scroll containers,
 * then nudge @reactour/tour to remeasure the mask.
 */
export function scrollTourTargetIntoView(selector) {
  if (!selector || typeof document === 'undefined') return false
  const el =
    typeof selector === 'string'
      ? document.querySelector(selector)
      : selector
  if (!el || typeof el.scrollIntoView !== 'function') return false
  el.scrollIntoView({
    behavior: 'auto',
    block: 'nearest',
    inline: 'nearest',
  })
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('resize'))
  }
  return true
}

/**
 * Keep tour cards inside the viewport. @reactour/tour places the popover
 * at 0,0 when a step has no selector, which clips the left edge.
 */
export function keepPopoverInView(base = {}) {
  const margin = 16
  const width = Number(base.width) || 400
  const height = Number(base.height) || 220
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800
  const left = Number.parseFloat(base.left) || 0
  const top = Number.parseFloat(base.top) || 0
  return {
    ...base,
    left: Math.max(margin, Math.min(left, Math.max(margin, vw - width - margin))),
    top: Math.max(margin, Math.min(top, Math.max(margin, vh - height - margin))),
  }
}

function darkPopoverStyle(base) {
  const isDark =
    typeof document !== 'undefined' &&
    document.documentElement.getAttribute('data-color-mode') === 'dark'
  if (!isDark) return base
  return {
    ...base,
    backgroundColor: '#2a2a2a',
    color: 'rgba(255, 255, 255, 0.87)',
  }
}

function mergeTourStyle(propStyle, extra) {
  return (base, state) => {
    const next =
      typeof propStyle === 'function'
        ? propStyle(base, state)
        : { ...base, ...propStyle }
    return extra(next, state)
  }
}

/**
 * Maps legacy reactour step `content({ goTo, inDOM  })` to @reactour/tour's
 * `content({ setCurrentStep, setIsOpen, ...  })`.
 */
export function mapLegacySteps(steps = []) {
  return steps.map((step) => {
    const withPosition =
      step.selector || step.position
        ? step
        : { ...step, position: 'center' }
    if (typeof withPosition.content !== 'function') return withPosition
    const legacyContent = withPosition.content
    return {
      ...withPosition,
      content: (props) =>
        legacyContent({
          goTo: (index) => props.setCurrentStep(index),
          inDOM: Boolean(props.isHighlightingObserved),
          ...props,
        }),
    }
  })
}

function TourBridge({ isOpen, onRequestClose, startAt = 0, steps = []  }) {
  const { setIsOpen, setCurrentStep, isOpen: open, currentStep } = useTour()
  const stepsRef = useRef(steps)
  stepsRef.current = steps

  useEffect(() => {
    if (isOpen) {
      scrollTourTargetIntoView(stepsRef.current[startAt]?.selector)
      setCurrentStep(startAt)
    }
    setIsOpen(Boolean(isOpen))
  }, [isOpen, setCurrentStep, setIsOpen, startAt])

  useEffect(() => {
    if (!isOpen) return undefined
    const selector = stepsRef.current[currentStep]?.selector
    if (!selector) return undefined
    const frame = window.requestAnimationFrame(() => {
      scrollTourTargetIntoView(selector)
    })
    return () => window.cancelAnimationFrame(frame)
  }, [isOpen, currentStep])

  useEffect(() => {
    if (isOpen && !open) {
      onRequestClose?.()
    }
  }, [isOpen, onRequestClose, open])

  return null
}

/**
 * Drop-in replacement for the unmaintained `reactour` default export,
 * backed by `@reactour/tour`.
 */
export default function Reactour({
  steps = [],
  isOpen = false,
  onRequestClose,
  update,
  startAt = 0,
  lastStepNextButton,
  disableKeyboardNavigation = false,
  disableDotsNavigation = false,
  showButtons = true,
  showNavigation,
  className,
  maskClassName,
  highlightedMaskClassName,
  accentColor,
  rounded,
  disableInteraction,
  onAfterOpen,
  onBeforeClose,
  styles: stylesProp,
  padding: paddingProp,
  ...rest
}) {
  const mappedSteps = useMemo(() => mapLegacySteps(steps), [steps])

  if (!isOpen) return null

  const styles = {
    ...stylesProp,
    popover: mergeTourStyle(stylesProp?.popover, (base, state) =>
      darkPopoverStyle(keepPopoverInView(base, state)),
    ),
  }
  if (accentColor) {
    styles.badge = mergeTourStyle(stylesProp?.badge, (base) => ({
      ...base,
      backgroundColor: accentColor,
    }))
  }
  if (rounded) {
    styles.maskArea = mergeTourStyle(stylesProp?.maskArea, (base) => ({
      ...base,
      rx: rounded,
    }))
  }

  return (
    <TourProvider
      key={update == null ? 'tour' : String(update)}
      steps={mappedSteps}
      defaultOpen
      startAt={startAt}
      disableKeyboardNavigation={disableKeyboardNavigation}
      disableDotsNavigation={disableDotsNavigation}
      disableInteraction={disableInteraction}
      showPrevNextButtons={showButtons}
      showNavigation={showNavigation ?? showButtons}
      showDots={!disableDotsNavigation}
      scrollSmooth={false}
      className={className}
      maskClassName={maskClassName}
      highlightedMaskClassName={highlightedMaskClassName}
      afterOpen={(target) => {
        scrollTourTargetIntoView(mappedSteps[startAt]?.selector)
        onAfterOpen?.(target)
      }}
      beforeClose={onBeforeClose}
      padding={paddingProp ?? { mask: 8, popover: 16 }}
      styles={styles}
      onClickClose={({ setIsOpen  }) => {
        setIsOpen(false)
        onRequestClose?.()
      }}
      onClickMask={({ setIsOpen  }) => {
        setIsOpen(false)
        onRequestClose?.()
      }}
      nextButton={({
        Button,
        currentStep,
        stepsLength,
        setIsOpen,
        setCurrentStep,
      }) => {
        const isLast = currentStep === stepsLength - 1
        if (isLast && lastStepNextButton) {
          return React.cloneElement(lastStepNextButton, {
            onClick: (event) => {
              lastStepNextButton.props?.onClick?.(event)
              setIsOpen(false)
              onRequestClose?.()
            },
          })
        }
        return (
          <Button
            onClick={() => {
              if (isLast) {
                setIsOpen(false)
                onRequestClose?.()
              } else {
                setCurrentStep((step) => step + 1)
              }
            }}
          />
        )
      }}
      {...rest}
    >
      <TourBridge
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        startAt={startAt}
        steps={mappedSteps}
      />
    </TourProvider>
  )
}
